import { useEffect, useMemo, useState } from 'react'
import { useAutoResizeTextarea } from '../useAutoResizeTextarea'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import Styles from './V4CampaignPreview.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { TopBar } from '../TopBar/TopBar'
import { V4StepFooter } from '../V4StepFooter'
import { useStepAction } from '../useStepAction'
import { useBrandPlanSchedules } from 'hooks/queries/schedules/useBrandPlanSchedules'
import { updateBrandPlanSchedule } from 'hooks/queries/schedules/useUpdateBrandPlanSchedule'
import { scheduleKeys } from 'hooks/queries/schedules/scheduleKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { BrandPlanSchedule, Theme, WeekDay } from 'types'
import { CampaignDatePicker } from 'screens/ContentPlan/CreateCampaignModal/CampaignDatePicker'
import { Button } from 'blaze-ui/Button'
import { ChevronDown2 } from 'blaze-ui/icons'
import dayjs from 'dayjs'

const TARGET_CAMPAIGN_COUNT = 4

interface Props {
  brandPlanId: number
  v4ScheduleId: number
  onNext: () => void
  onBack: () => void
}

interface CampaignEditState {
  title: string
  startDate: string
  length: number
  weekDays: WeekDay[]
  themeDescription: string
  selectedThemeId: string | undefined
  themes: Theme[]
}

export const V4CampaignPreview = ({ brandPlanId, v4ScheduleId, onNext, onBack }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const { data, isLoading } = useBrandPlanSchedules(
    { brandPlanId, order_direction: 'asc', items: TARGET_CAMPAIGN_COUNT },
    {
      refetchInterval: (queryData) => {
        const schedules = queryData?.pages?.flatMap((p) => p.data.schedules) ?? []
        const allReady = schedules.length >= TARGET_CAMPAIGN_COUNT && schedules.every((s) => s.themes?.length > 0)
        return allReady ? false : 3000
      },
    },
  )

  const schedules = useMemo(() => data?.pages?.flatMap((p) => p.data.schedules) ?? [], [data])
  const firstCampaign = schedules.find((s) => s.id === v4ScheduleId)
  const editableCampaigns = useMemo(
    () => schedules.filter((s) => s.id !== v4ScheduleId).slice(0, 3),
    [schedules, v4ScheduleId],
  )

  const [editStates, setEditStates] = useState<Record<number, CampaignEditState>>({})
  const [initialized, setInitialized] = useState<Set<number>>(new Set())
  const { isSaving, run } = useStepAction()

  useEffect(() => {
    for (const schedule of editableCampaigns) {
      if (initialized.has(schedule.id) || !schedule.themes?.length) continue

      const firstTheme = schedule.theme ?? schedule.themes[0]
      setInitialized((prev) => new Set(prev).add(schedule.id))
      setEditStates((prev) => ({
        ...prev,
        [schedule.id]: {
          title: schedule.title ?? firstTheme?.title ?? '',
          startDate: schedule.start_date,
          length: schedule.length,
          weekDays: schedule.week_days,
          themeDescription: firstTheme?.description ?? '',
          selectedThemeId: firstTheme?.id,
          themes: schedule.themes,
        },
      }))
    }
  }, [editableCampaigns, initialized])

  const updateEditState = (scheduleId: number, patch: Partial<CampaignEditState>) => {
    setEditStates((prev) => ({
      ...prev,
      [scheduleId]: { ...prev[scheduleId], ...patch },
    }))
  }

  const handleNext = () =>
    run(async () => {
      const updates = editableCampaigns
        .filter((s) => editStates[s.id])
        .map((s) => {
          const state = editStates[s.id]
          const updatedThemes = state.themes.map((t) =>
            t.id === state.selectedThemeId ? { ...t, title: state.title, description: state.themeDescription } : t,
          )
          return updateBrandPlanSchedule({
            id: s.id,
            workspaceId,
            start_date: state.startDate,
            length: state.length,
            week_days: state.weekDays,
            theme: state.selectedThemeId,
            themes: updatedThemes,
          })
        })

      await Promise.all(updates)

      await Promise.all([
        ...editableCampaigns
          .filter((s) => editStates[s.id])
          .map((s) => queryClient.invalidateQueries(scheduleKeys.detail(s.id))),
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brandPlans', brandPlanId, 'schedules', 'list']),
      ])

      onNext()
    }, 'Something went wrong saving your campaigns. Please try again.')

  const allScheduleIds = schedules.map((s) => s.id)

  return (
    <MultiStepStepLayout headingLevel={isMobile ? 2 : 1} scrollContainerClassName={Styles.scrollContainer}>
      <V4StepFooter onBack={onBack} onNext={handleNext} isSaving={isSaving} isDisabled={isLoading} alwaysShowBack>
        <div className={Styles.content}>
          <TopBar highlightStep="finishingUp" />

          <div className={Styles.headerSection}>
            <Heading level={isMobile ? 2 : 1}>Your first four campaigns are ready!</Heading>
            <Text color="var(--dark-60)">
              Tweak any of the themes or regenerate them before they generate their content
            </Text>
          </div>

          <div className={Styles.campaignCards}>
            {firstCampaign && (
              <ReadOnlyCampaignCard
                schedule={firstCampaign}
                campaignNumber={allScheduleIds.indexOf(firstCampaign.id) + 1}
              />
            )}

            {editableCampaigns.map((schedule) => {
              const state = editStates[schedule.id]
              const campaignNumber = allScheduleIds.indexOf(schedule.id) + 1
              if (!state) {
                return <CampaignSkeleton key={schedule.id} campaignNumber={campaignNumber} />
              }
              return (
                <EditableCampaignCard
                  key={schedule.id}
                  campaignNumber={campaignNumber}
                  state={state}
                  onChange={(patch) => updateEditState(schedule.id, patch)}
                />
              )
            })}

            {Array.from({ length: Math.max(0, 3 - editableCampaigns.length) }).map((_, i) => (
              <CampaignSkeleton key={`skeleton-${i}`} campaignNumber={i + 2} />
            ))}
          </div>
        </div>
      </V4StepFooter>
    </MultiStepStepLayout>
  )
}

function formatDateRange(startDate?: string, endDate?: string): string {
  if (!startDate) return ''
  const start = dayjs(startDate).format('MMM D')
  if (!endDate) return start
  return `${start} - ${dayjs(endDate).format('MMM D')}`
}

function ReadOnlyCampaignCard({ schedule, campaignNumber }: { schedule: BrandPlanSchedule; campaignNumber: number }) {
  const theme = schedule.theme ?? schedule.themes?.[0]
  const dateRange = formatDateRange(schedule.start_date, schedule.end_date)

  return (
    <div className={Styles.cardGenerating}>
      <div className={Styles.cardSidebar}>
        <Text variant="secondary" color="var(--dark-40)">
          Campaign {campaignNumber}
        </Text>
        <Text variant="secondary" color="var(--dark-90)">
          {dateRange}
        </Text>
      </div>
      <div className={Styles.cardBody}>
        <p className={Styles.campaignTitle}>{schedule.title ?? theme?.title ?? `Campaign ${campaignNumber}`}</p>
        {theme?.description && <p className={Styles.campaignDescription}>{theme.description}</p>}
      </div>
      <span className={Styles.statusBadgeGenerating}>Generating...</span>
    </div>
  )
}

function EditableCampaignCard({
  campaignNumber,
  state,
  onChange,
}: {
  campaignNumber: number
  state: CampaignEditState
  onChange: (patch: Partial<CampaignEditState>) => void
}) {
  const { ref: textareaRef, resize: autoResize } = useAutoResizeTextarea(state.themeDescription)

  const endDate = dayjs(state.startDate)
    .add(state.length * 7 - 1, 'day')
    .format('YYYY-MM-DD')
  const dateRangeText = formatDateRange(state.startDate, endDate)

  return (
    <div className={Styles.card}>
      <div className={Styles.cardSidebar}>
        <Text variant="secondary" color="var(--dark-40)">
          Campaign {campaignNumber}
        </Text>
        <CampaignDatePicker
          startDate={state.startDate}
          length={state.length}
          weekDays={state.weekDays}
          onDateChange={(startDate, _endDate, length) => onChange({ startDate, length })}
          onWeekDaysChange={(weekDays) => onChange({ weekDays })}
          renderTrigger={() => (
            <Button
              variant="ghost"
              size="sm"
              className={Styles.dateButton}
              endIcon={ChevronDown2}
              endIconClassName={Styles.dateChevron}
            >
              <span className={Styles.dateText}>{dateRangeText}</span>
            </Button>
          )}
        />
      </div>
      <div className={Styles.cardBody}>
        <input
          className={Styles.titleTextarea}
          value={state.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Campaign name"
        />
        <textarea
          ref={textareaRef}
          className={Styles.themeTextarea}
          value={state.themeDescription}
          onChange={(e) => {
            onChange({ themeDescription: e.target.value })
            autoResize()
          }}
          placeholder="Describe the theme for this campaign"
        />
      </div>
    </div>
  )
}

function CampaignSkeleton({ campaignNumber }: { campaignNumber: number }) {
  return (
    <div className={Styles.cardSkeleton}>
      <div className={Styles.cardSidebar}>
        <Text variant="secondary" color="var(--dark-40)">
          Campaign {campaignNumber}
        </Text>
      </div>
      <div className={Styles.cardBody}>
        <div className={Styles.skeletonLine} />
        <div className={Styles.skeletonBlock} />
      </div>
    </div>
  )
}
